/*
 * Theme store — light / dark / system (B2).
 *
 * The chosen mode persists in localStorage ('xai_theme'). The resolved
 * theme is applied as `data-theme="light|dark"` on <html>; both the app
 * tokens (assets/styles.css) and PrimeVue (darkModeSelector in main.ts)
 * key off that attribute. "system" follows prefers-color-scheme live via
 * a matchMedia listener. `init()` runs in main.ts BEFORE the app mounts
 * so the first paint is already in the right theme (no flash).
 */

import { defineStore } from "pinia";

export type ThemeMode = "light" | "dark" | "system";

const LS_THEME = "xai_theme";
const MODES: ThemeMode[] = ["light", "dark", "system"];

function storedMode(): ThemeMode {
  const raw = localStorage.getItem(LS_THEME);
  return MODES.includes(raw as ThemeMode) ? (raw as ThemeMode) : "system";
}

export const useThemeStore = defineStore("theme", {
  state: () => ({
    mode: storedMode(),
    systemDark:
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-color-scheme: dark)").matches,
  }),

  getters: {
    /** The theme actually in effect right now. */
    resolved(state): "light" | "dark" {
      if (state.mode === "system") return state.systemDark ? "dark" : "light";
      return state.mode;
    },
  },

  actions: {
    /** Apply the resolved theme + start following OS changes. Call once, early. */
    init(): void {
      if (typeof window.matchMedia === "function") {
        const mq = window.matchMedia("(prefers-color-scheme: dark)");
        this.systemDark = mq.matches;
        const onChange = (e: MediaQueryListEvent) => {
          this.systemDark = e.matches;
          this.apply();
        };
        // Older Safari only has addListener.
        if (typeof mq.addEventListener === "function") mq.addEventListener("change", onChange);
        else if (typeof (mq as any).addListener === "function") (mq as any).addListener(onChange);
      }
      this.apply();
    },

    apply(): void {
      document.documentElement.setAttribute("data-theme", this.resolved);
    },

    setMode(mode: ThemeMode): void {
      this.mode = mode;
      localStorage.setItem(LS_THEME, mode);
      this.apply();
    },

    /** light → dark → system → light … (the AppShell toggle). */
    cycle(): void {
      const next = MODES[(MODES.indexOf(this.mode) + 1) % MODES.length];
      this.setMode(next);
    },
  },
});
